/**
 * This file was copied from dashboard into clowncar as part of a cleanup effort
 * ...and has been modified a lot since
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { LocationDescriptor } from 'history';
import { Redirect, Route, RouteComponentProps } from 'react-router-dom';
import { clearCsrfInterval, setupCsrfTokenRefresh } from '@tock/shared/utils/csrfTokenUtils';
import { find, isNil } from 'es-toolkit/compat';
import { useActions } from 'utils/connect';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';

interface Protection {
  requireThat: boolean;
  elseRedirectTo: LocationDescriptor | null;
}

interface Props {
  component?:
    | React.ComponentType<React.PropsWithChildren<RouteComponentProps<any>>>
    | React.ComponentType<React.PropsWithChildren<any>>;
  render?: (props: RouteComponentProps<any>) => React.ReactNode;
  path?: string;
  protection: Protection | Array<Protection>;
}

const defaultProtection: Protection = {
  requireThat: false,
  elseRedirectTo: null,
};

const ProtectedRoute: React.FC<Props> = ({
  component,
  protection = defaultProtection,
  render,
  ...rest
}) => {
  const { actions } = useActions();
  const hasEnableCsrfToken = useFeatureFlag('ENABLE_CSRF_TOKEN');
  const csrfInterval = React.useRef<NodeJS.Timeout | null>(null);

  const setupCsrf = async () => {
    await setupCsrfTokenRefresh(
      actions.csrfTokenActions.getCsrfToken,
      hasEnableCsrfToken,
      false,
      csrfInterval
    );
  };

  // Compute firstUnmetProtection once per render
  const firstUnmetProtection = React.useMemo(
    () =>
      find(
        Array.isArray(protection) ? protection : [protection],
        (protection) => !protection.requireThat
      ),
    [protection]
  );

  // Determine authentication state and setup CSRF when needed
  React.useEffect(() => {
    const allowed = isNil(firstUnmetProtection);
    if (allowed && hasEnableCsrfToken) {
      setupCsrf();
    } else if (csrfInterval.current) {
      clearCsrfInterval(csrfInterval);
    }
    // Cleanup on unmount
    return () => {
      clearCsrfInterval(csrfInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstUnmetProtection, hasEnableCsrfToken]);

  const internalRender = (props: RouteComponentProps) => {
    if (isNil(firstUnmetProtection)) {
      if (component) {
        return React.createElement(component, props);
      } else if (render) {
        return render(props);
      } else {
        return null;
      }
    }
    if (firstUnmetProtection.elseRedirectTo) {
      const toPart =
        typeof firstUnmetProtection.elseRedirectTo === 'string'
          ? { pathname: firstUnmetProtection.elseRedirectTo }
          : firstUnmetProtection.elseRedirectTo;
      const to = {
        ...toPart,
        state: {
          ...(toPart.state as object),
          from: props.location,
        },
      };
      return <Redirect to={to} />;
    }
    return null;
  };

  return <Route {...rest} render={internalRender} />;
};

export default ProtectedRoute;
